import { CardSelector, SliderQuestion, MultiSelectQuestion, TextAreaQuestion, StackedOptions, SubmitQuestion } from './QuestionTypes';

const FREQ_OPTIONS = [
  { val: 'every_day', label: 'Every day', icon: '🔥' },
  { val: 'few_times_week', label: 'Few times a week', icon: '⚡' },
  { val: 'once_week', label: 'Once a week', icon: '📅' },
  { val: 'less_often', label: 'Less often', icon: '🌱' }
];

const FEATURES = [
  { val: 'pi_score', label: 'PI Score & Emotion', icon: 'π' },
  { val: 'grammar', label: 'Grammar Check', icon: '✎' },
  { val: 'intent', label: 'Intent Selector', icon: '🎯' },
  { val: 'content_gen', label: 'Content Generator', icon: '✦' },
  { val: 'prompt_copilot', label: 'Prompt Copilot', icon: '⚡' },
  { val: 'rewrite_tones', label: 'Rewrite Tones', icon: '🔄' }
];

const WOULD_MISS_OPTIONS = [
  { val: 'miss_a_lot', label: 'Yes — I would genuinely miss it a lot', color: 'from-green-600/30 to-green-500/10 border-green-500/30' },
  { val: 'notice_gone', label: 'Yes, I would notice it was gone', color: 'from-blue-600/30 to-blue-500/10 border-blue-500/30' },
  { val: 'not_big_deal', label: 'Probably not, not a big deal', color: 'from-amber-600/30 to-amber-500/10 border-amber-500/30' },
  { val: 'would_not_miss', label: 'No, I would not miss it', color: 'from-red-600/30 to-red-500/10 border-red-500/30' }
];

const RECOMMEND_OPTIONS = [
  { val: 'recommend_now', label: 'Yes, I would recommend it right now', color: 'from-green-600/30 to-green-500/10 border-green-500/30' },
  { val: 'recommend_later', label: 'Probably, once it improves a bit', color: 'from-blue-600/30 to-blue-500/10 border-blue-500/30' },
  { val: 'maybe', label: 'Maybe, not sure yet', color: 'from-amber-600/30 to-amber-500/10 border-amber-500/30' },
  { val: 'no', label: 'No, not in its current state', color: 'from-red-600/30 to-red-500/10 border-red-500/30' }
];

const USER_TYPES = [
  { val: 'student', label: 'Student', icon: '🎓' },
  { val: 'job_seeker', label: 'Job seeker', icon: '🔍' },
  { val: 'sales_pro', label: 'Sales pro', icon: '📈' },
  { val: 'founder', label: 'Founder', icon: '🚀' },
  { val: 'manager', label: 'Manager', icon: '👥' },
  { val: 'non_native', label: 'Non-native English speaker', icon: '🌏' }
];

export function FeedbackQuestionRenderer({ question, answers, setAnswer, step, setStep, canGoNext, next, handleSubmit, submitting }) {
  const q = question;

  switch (q.id) {
    case 'usage_frequency':
      return <CardSelector questionId="freq" title="How often do you use SendPI?" options={FREQ_OPTIONS}
        value={answers.usage_frequency} onSelect={(v) => setAnswer('usage_frequency', v)} step={step} setStep={setStep} />;

    case 'value_score':
      return <SliderQuestion value={answers.value_score || 5} onChange={(v) => setAnswer('value_score', v)} next={next} canGoNext={canGoNext} />;

    case 'features_used': {
      const selected = answers.features_used || [];
      const toggleFeature = (v) => {
        const updated = selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v];
        setAnswer('features_used', updated);
      };
      return <MultiSelectQuestion title="Which features have you actually used?" subtitle="Select all that apply"
        features={FEATURES} selected={selected} onToggle={toggleFeature} next={next} canGoNext={canGoNext} />;
    }

    case 'likes_most':
      return <TextAreaQuestion questionId="likes_most" title="What do you love most about SendPI?"
        placeholder="The moment when..." value={answers.likes_most} onChange={(v) => setAnswer('likes_most', v)}
        next={next} canGoNext={canGoNext} minLength={20} />;

    case 'frustrations':
      return <TextAreaQuestion questionId="frustrations" title="What frustrates you about SendPI?"
        subtitle="Be honest. Nothing is off-limits. This is the most important answer we will read."
        placeholder="What made you think 'ugh' while using it?" value={answers.frustrations}
        onChange={(v) => setAnswer('frustrations', v)} next={next} canGoNext={canGoNext} minLength={20}
        borderColor="border-red-500/20" focusBorder="focus:border-red-500/50" />;

    case 'missing_feature':
      return <TextAreaQuestion questionId="missing_feature" title="What one feature would make you use SendPI every single day?"
        placeholder="If only SendPI could..." value={answers.missing_feature}
        onChange={(v) => setAnswer('missing_feature', v)} next={next} canGoNext={canGoNext} />;

    case 'would_miss':
      return <StackedOptions questionId="miss" title="If SendPI stopped working today..." subtitle="...would you miss it?"
        options={WOULD_MISS_OPTIONS} value={answers.would_miss} onSelect={(v) => setAnswer('would_miss', v)} step={step} setStep={setStep} />;

    case 'would_recommend':
      return <StackedOptions questionId="recommend" title="Would you recommend SendPI to a friend or colleague?"
        options={RECOMMEND_OPTIONS} value={answers.would_recommend} onSelect={(v) => setAnswer('would_recommend', v)} step={step} setStep={setStep} />;

    case 'user_type':
      return <CardSelector questionId="type" title="Which best describes you?" options={USER_TYPES}
        value={answers.user_type} onSelect={(v) => setAnswer('user_type', v)} step={step} setStep={setStep} />;

    case 'additional_comments':
      return <SubmitQuestion value={answers.additional_comments} onChange={(v) => setAnswer('additional_comments', v)}
        handleSubmit={handleSubmit} submitting={submitting} />;

    default:
      return null;
  }
}
